import { Link } from "react-router-dom";
import { Receipt, FileCheck, Building2, CalendarClock, Undo2, PiggyBank, CalendarDays } from "lucide-react";
import { PageHeader } from "@/components/site/PageHeader";
import { Reveal } from "@/components/site/Reveal";
import { CONTACT } from "@/data";

const TAX_SERVICES = [
  { icon: Receipt, title: "Individual Tax Filing", desc: "Accurate federal and state returns for individuals and families — W-2s, 1099s, dependents, and everything in between." },
  { icon: FileCheck, title: "Maximized Credits & Deductions", desc: "Child Tax Credit, Earned Income Credit, education credits, homeowner deductions — we dig for every dollar you're owed." },
  { icon: Building2, title: "Small Business & Self-Employed", desc: "Schedule C filings, quarterly estimates, business deductions, and bookkeeping guidance for entrepreneurs and 1099 earners." },
  { icon: Undo2, title: "Amendments & Back Taxes", desc: "Missed a year or made a mistake? We help you amend prior returns and get back in good standing with the IRS." },
  { icon: CalendarClock, title: "Year-Round Tax Planning", desc: "Taxes aren't a once-a-year event. We plan withholdings, retirement contributions, and life changes before they cost you." },
  { icon: PiggyBank, title: "Tax-Smart Protection", desc: "Life insurance and retirement strategies coordinated with your tax picture — so your legacy transfers tax-efficiently." },
];

const STEPS = [
  { n: "01", title: "Gather", desc: "Send us your documents — W-2s, 1099s, ID, and last year's return. We'll tell you exactly what's needed." },
  { n: "02", title: "Prepare", desc: "We prepare your return, hunting every credit and deduction your household qualifies for." },
  { n: "03", title: "Review & File", desc: "You review and approve everything before we e-file. No surprises, ever." },
  { n: "04", title: "Plan Ahead", desc: "We close with a look at next year — withholdings, savings, and protection moves that keep more in your pocket." },
];

export default function TaxesPage() {
  return (
    <>
      <PageHeader
        overline="Tax Services"
        title="Keep more of what"
        accent="you earn."
        subtitle="Professional tax preparation and filing with Heritage Family Protection Agency — accurate, maximized, and planned around your family's whole financial picture."
      />

      <section className="relative py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {TAX_SERVICES.map((s, i) => (
              <Reveal key={s.title} delay={(i % 3) * 0.06}>
                <div data-testid={`tax-service-${i}`} className="h-full rounded-3xl border border-white/10 bg-[#0a192f] p-8 transition-colors duration-500 hover:border-[#bf953f]/45">
                  <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl border border-[#bf953f]/30 text-[#d4af37]">
                    <s.icon size={22} strokeWidth={1.5} />
                  </span>
                  <h3 className="mt-5 font-serif-display text-2xl text-white">{s.title}</h3>
                  <p className="mt-2 font-body font-light text-sm md:text-base leading-relaxed text-white/60">{s.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="relative py-20 md:py-28 bg-[#0a192f] border-y border-[#bf953f]/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <p className="font-body text-xs md:text-sm tracking-[0.3em] uppercase text-[#d4af37]">How Filing Works</p>
            <h2 className="mt-4 font-serif-display text-4xl sm:text-5xl md:text-6xl font-medium tracking-tight text-white">
              Four steps, <span className="text-gold-gradient italic">zero stress.</span>
            </h2>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-4 gap-8">
            {STEPS.map((st, i) => (
              <Reveal key={st.n} delay={i * 0.07}>
                <div className="border-t border-[#bf953f]/25 pt-6 h-full">
                  <span className="font-serif-display text-sm text-[#d4af37]">{st.n}</span>
                  <h3 className="mt-3 font-serif-display text-2xl text-white">{st.title}</h3>
                  <p className="mt-3 font-body font-light text-sm md:text-base leading-relaxed text-white/60">{st.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="relative py-20 md:py-28">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <Reveal>
            <h2 className="font-serif-display text-4xl sm:text-5xl md:text-6xl font-medium tracking-tight text-white">
              Ready to file <span className="text-gold-gradient italic">with us?</span>
            </h2>
            <p className="mt-5 mx-auto max-w-xl font-body font-light text-base md:text-lg text-white/60">
              Book a tax consultation or call us — we'll tell you exactly what documents to bring
              and what refunds or savings to expect.
            </p>
            <div className="mt-10 flex flex-wrap justify-center gap-4">
              <Link
                to="/book-a-call"
                data-testid="taxes-book-btn"
                className="btn-sweep inline-flex items-center gap-2 rounded-full bg-gold-metallic px-8 py-3.5 font-body text-sm font-medium tracking-wide text-black"
              >
                <CalendarDays size={16} /> Book a Tax Consultation
              </Link>
              <a
                href={CONTACT.phoneHref}
                data-testid="taxes-call-btn"
                className="inline-flex items-center rounded-full border border-[#bf953f]/40 px-8 py-3.5 font-body text-sm tracking-wide text-white/80 hover:text-[#fcf6ba] hover:border-[#d4af37] transition-colors duration-300"
              >
                Call {CONTACT.phone}
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
